import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../api';
import { useCoinBalance } from '../../../contexts/CoinBalanceContext';
import AudioQuestionCard from '../lifeskills/AudioQuestionCard';

// The endpoints the student quiz page (pages/student/StudentQuizPage.jsx) uses for a course item, so
// grading, ISF coins (capped by the item's admin coins) and the item's progress stay the same.
const quizApi = (studentId) => `/api/v2/lms/student/${studentId}/courses/computer-apps/quiz`;

const questionIdOf = (question) => question.id || question._id;
const questionTextOf = (question) => question.questionText || question.question || question.text;
const isAnswered = (question, answer) => (question.type === 'fill_blank' ? Boolean(answer && answer.trim()) : Boolean(answer));
const optionsOf = (question) => {
  const options = question.options || [];
  if (question.type === 'true_false' && options.length === 0) {
    return [
      { id: 'True', text: 'True' },
      { id: 'False', text: 'False' },
    ];
  }
  return options;
};
const errorMessageOf = (data, fallback) => data?.error || data?.message || fallback;

const performanceOf = (score) =>
  score >= 90 ? { emoji: '🌟', text: 'Outstanding!' }
    : score >= 80 ? { emoji: '🎉', text: 'Excellent!' }
      : score >= 60 ? { emoji: '👍', text: 'Good Job!' }
        : score >= 40 ? { emoji: '💪', text: 'Keep Trying!' }
          : { emoji: '📚', text: 'Practice More!' };

const backButton = 'shrink-0 rounded-xl border-2 border-violet-200 bg-white px-4 py-2 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-50';

function QuizResults({ results, onRetry, onClose }) {
  const { score, correctAnswers, totalQuestions, coinsEarned, alreadyEarned, breakdown = [] } = results;
  const performance = performanceOf(score);

  return (
    <div>
      <div className="rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50 p-6 text-center">
        <h3 className="text-2xl font-extrabold text-slate-900">
          Quiz Complete! {performance.emoji}
        </h3>
        <p className="font-bold text-gray-600">{performance.text}</p>
        <p className="mt-3 text-6xl font-extrabold text-violet-700">{score}%</p>
        <p className="mt-1 text-lg font-bold text-gray-700">
          {correctAnswers} out of {totalQuestions} correct
        </p>
        <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-5 py-2 font-extrabold text-amber-700">
          🪙 {coinsEarned} ISF Coins earned
        </p>
        {alreadyEarned && (
          <p className="mt-3 text-sm font-semibold text-blue-700">
            Coins are only awarded on the first submission, but you can keep practicing! 🌟
          </p>
        )}
      </div>

      <p
        className={`mt-4 rounded-xl px-4 py-3 text-center font-bold ${
          score >= 80 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
        }`}
      >
        {score >= 80
          ? '🎊 Amazing work! Keep it up!'
          : '💪 Keep going! You can retry the quiz to improve your score. This task is already counted as completed.'}
      </p>

      {breakdown.length > 0 && (
        <div className="mt-5">
          <h4 className="mb-3 text-lg font-extrabold text-slate-800">📊 Question Breakdown</h4>
          <ol className="space-y-3">
            {breakdown.map((item, index) => (
              <li
                key={String(item.questionId || index)}
                className={`rounded-xl border-2 p-4 ${item.isCorrect ? 'border-green-200 bg-green-50' : 'border-rose-200 bg-rose-50'}`}
              >
                <p className="font-extrabold text-slate-800">
                  {item.isCorrect ? '✅' : '❌'} Question {index + 1}
                </p>
                <p className="mt-1 font-semibold text-gray-700 break-words">{item.question}</p>
                <p className={`mt-1 text-sm font-bold ${item.isCorrect ? 'text-green-700' : 'text-rose-700'}`}>
                  Your answer: {item.studentAnswer}
                </p>
                {!item.isCorrect && (
                  <p className="text-sm font-bold text-green-700">Correct answer: {item.correctAnswer}</p>
                )}
                {item.explanation && (
                  <p className="mt-2 rounded-lg bg-white px-3 py-2 text-sm text-gray-600">💡 {item.explanation}</p>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={onRetry}
          className="rounded-xl bg-violet-600 px-5 py-3 font-extrabold text-white transition-colors hover:bg-violet-700"
        >
          🔄 Retry Quiz
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl bg-gray-100 px-5 py-3 font-extrabold text-gray-700 transition-colors hover:bg-gray-200"
        >
          ← Back to lesson
        </button>
      </div>
    </div>
  );
}

/**
 * A course item's quiz, taken inside the Learning Zone (in place of the chapter's
 * cards) instead of on the separate student quiz page: one question at a time,
 * then the score, ISF coins and a per-question breakdown.
 * `courseId` / `contentItemId` tie the attempt to the item, as the quiz page's
 * navigation state did.
 */
export default function LearningZoneQuiz({ title, quizId, courseId, contentItemId, onClose }) {
  const { refreshBalance } = useCoinBalance();
  const topRef = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [audioCompleted, setAudioCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  // The Start Quiz button sits low in the chapter card, so bring the quiz's top into view.
  useEffect(() => {
    topRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [attempt, results]);

  useEffect(() => {
    let cancelled = false;
    const studentId = localStorage.getItem('userId') || 'student1';
    setLoading(true);
    setError(null);
    setQuiz(null);
    setIndex(0);
    setAnswers({});
    setResults(null);

    api
      .get(`${quizApi(studentId)}/${quizId}`)
      .then((response) => {
        if (cancelled) return;
        if (response.data.success && response.data.quiz && response.data.quiz.questions) {
          setQuiz(response.data.quiz);
        } else {
          const message = errorMessageOf(response.data, 'Failed to load quiz questions');
          setError(message);
          toast.error(message);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message = errorMessageOf(err.response?.data, 'Failed to load quiz questions');
        setError(message);
        toast.error(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [quizId, attempt]);

  const questions = quiz?.questions || [];
  const current = questions[index];

  // An audio question has to be heard to the end before it can be answered.
  useEffect(() => {
    setAudioCompleted(Boolean(current && !current.audioUrl));
  }, [current]);

  const header = (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-violet-700">
          ❓ Quiz
        </span>
        <h2 className="mt-2 text-[26px] font-extrabold leading-tight text-slate-900 break-words">{title}</h2>
        {quiz?.title && quiz.title !== title && (
          <p className="text-sm font-semibold text-gray-500 break-words">{quiz.title}</p>
        )}
      </div>
      <button type="button" onClick={onClose} className={backButton}>
        ← Back to lesson
      </button>
    </div>
  );

  const shell = (body) => (
    <section
      ref={topRef}
      aria-label={title}
      className="scroll-mt-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
    >
      {header}
      {body}
    </section>
  );

  if (loading) {
    return shell(
      <p role="status" className="py-12 text-center font-bold text-gray-500">
        Loading quiz...
      </p>
    );
  }

  if (error || questions.length === 0) {
    return shell(
      <div className="rounded-2xl bg-violet-50 px-6 py-10 text-center">
        <p className="text-5xl">🤔</p>
        <p className="mt-3 text-xl font-extrabold text-rose-600">{error || 'No questions found'}</p>
        {!error && (
          <p className="mt-1 font-semibold text-gray-500">This quiz has no questions yet. Please ask your teacher.</p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="mt-5 rounded-xl bg-violet-600 px-6 py-3 font-extrabold text-white transition-colors hover:bg-violet-700"
        >
          Back to lesson
        </button>
      </div>
    );
  }

  if (results) {
    return shell(<QuizResults results={results} onRetry={() => setAttempt((count) => count + 1)} onClose={onClose} />);
  }

  const currentId = questionIdOf(current);
  const currentAnswer = answers[currentId];
  const locked = !audioCompleted && Boolean(current.audioUrl);
  const isLast = index === questions.length - 1;
  const answeredCount = questions.filter((question) => isAnswered(question, answers[questionIdOf(question)])).length;
  const canSubmit = answeredCount === questions.length && !submitting;
  const setAnswer = (value) => setAnswers((previous) => ({ ...previous, [currentId]: value }));

  const handleSubmit = async () => {
    const remaining = questions.length - answeredCount;
    if (remaining > 0) {
      toast.error(`Please answer all questions (${remaining} remaining)`);
      return;
    }
    try {
      setSubmitting(true);
      const studentId = localStorage.getItem('userId') || 'student1';
      const response = await api.post(`${quizApi(studentId)}/submit`, {
        quizId,
        courseId: courseId || quiz.course?._id || quiz.course || null,
        contentItemId,
        answers: questions.map((question) => {
          const id = questionIdOf(question);
          const answer = answers[id];
          return {
            questionId: id,
            selectedOptionId: question.type === 'fill_blank' ? null : answer,
            answerText: question.type === 'fill_blank' ? answer : null,
          };
        }),
      });
      if (response.data.success) {
        toast.success('Quiz submitted successfully!');
        await refreshBalance();
        setResults(response.data.results);
      } else {
        toast.error(errorMessageOf(response.data, 'Failed to submit quiz'));
      }
    } catch (err) {
      toast.error(errorMessageOf(err.response?.data, 'Failed to submit quiz'));
    } finally {
      setSubmitting(false);
    }
  };

  return shell(
    <>
      <div className="mb-5">
        <div className="mb-2 flex items-center justify-between text-sm font-bold text-gray-500">
          <span>
            Question {index + 1} of {questions.length}
          </span>
          <span>
            {answeredCount} / {questions.length} answered
          </span>
        </div>
        <div className="h-3 w-full rounded-full bg-violet-100">
          <div
            className="h-3 rounded-full bg-violet-500 transition-all duration-300"
            style={{ width: `${((index + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {current.audioUrl && (
        <AudioQuestionCard
          key={currentId}
          audioUrl={current.audioUrl}
          questionText={questionTextOf(current)}
          onAudioComplete={() => setAudioCompleted(true)}
          autoPlay
        />
      )}

      <div className="mb-5 rounded-2xl bg-violet-50 p-5">
        <h3 className="mb-4 text-xl font-extrabold text-slate-800 break-words">{questionTextOf(current)}</h3>

        {current.type === 'fill_blank' ? (
          <>
            <input
              type="text"
              aria-label="Your answer"
              placeholder="Type your answer here..."
              value={currentAnswer || ''}
              onChange={(event) => setAnswer(event.target.value)}
              onBlur={(event) => setAnswer(event.target.value.trim())}
              disabled={locked}
              className="w-full rounded-xl border-2 border-violet-200 bg-white px-4 py-3 font-semibold text-slate-800 focus:border-violet-500 focus:outline-none disabled:opacity-50"
            />
            {!isAnswered(current, currentAnswer) && (
              <p className="mt-2 text-sm font-semibold text-gray-500">Type your answer to continue.</p>
            )}
          </>
        ) : (
          <div role="radiogroup" aria-label={questionTextOf(current)} className="space-y-3">
            {optionsOf(current).map((option, optionIndex) => {
              const optionId = option.id || option._id;
              const selected = currentAnswer === optionId;
              return (
                <label
                  key={optionId}
                  className={`relative flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition-colors focus-within:ring-2 focus-within:ring-violet-300 ${
                    selected ? 'border-violet-500 bg-violet-100' : 'border-gray-200 bg-white hover:border-violet-300'
                  } ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <input
                    type="radio"
                    name={`question-${currentId}`}
                    value={optionId}
                    checked={selected}
                    onChange={() => setAnswer(optionId)}
                    disabled={locked}
                    className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-extrabold ${
                      selected ? 'bg-violet-600 text-white' : 'bg-violet-100 text-violet-700'
                    }`}
                  >
                    {String.fromCharCode(65 + optionIndex)}
                  </span>
                  <span className="font-bold text-slate-800 break-words">{option.text}</span>
                </label>
              );
            })}
          </div>
        )}

        {locked && (
          <p className="mt-4 rounded-lg bg-amber-50 p-3 text-sm font-semibold text-amber-700">
            🎧 Please listen to the complete audio before selecting an answer
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setIndex(index - 1)}
          disabled={index === 0}
          className="rounded-xl bg-gray-100 px-5 py-2.5 font-extrabold text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          ← Previous
        </button>

        <span className="flex-1 text-center text-sm font-bold text-green-600">
          {isAnswered(current, currentAnswer) ? '✓ Answer saved' : ''}
        </span>

        {isLast ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`rounded-xl px-6 py-2.5 font-extrabold transition-colors ${
              canSubmit ? 'bg-green-500 text-white hover:bg-green-600' : 'cursor-not-allowed bg-gray-200 text-gray-500'
            }`}
          >
            {submitting ? 'Submitting...' : 'Submit Quiz 🎉'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setIndex(index + 1)}
            className="rounded-xl bg-violet-600 px-6 py-2.5 font-extrabold text-white transition-colors hover:bg-violet-700"
          >
            Next →
          </button>
        )}
      </div>

      <div className="mt-6 border-t border-gray-100 pt-4">
        <p className="mb-2 text-sm font-extrabold text-slate-700">Answer Summary</p>
        <div className="flex flex-wrap gap-2">
          {questions.map((question, questionIndex) => {
            const id = questionIdOf(question);
            const isCurrent = questionIndex === index;
            const done = isAnswered(question, answers[id]);
            return (
              <button
                key={id}
                type="button"
                onClick={() => setIndex(questionIndex)}
                aria-label={`Go to question ${questionIndex + 1}`}
                aria-current={isCurrent ? 'step' : undefined}
                className={`h-10 w-10 rounded-xl text-sm font-extrabold transition-colors ${
                  isCurrent
                    ? 'bg-violet-600 text-white ring-2 ring-violet-300'
                    : done
                      ? 'border-2 border-green-400 bg-green-100 text-green-700'
                      : 'border-2 border-gray-200 bg-gray-100 text-gray-500'
                }`}
              >
                {questionIndex + 1}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
